import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  MapPin, Hash, Pencil, Package,
  MessageCircle, AtSign, TrendingUp, Tag, Users,
} from 'lucide-react';
import './ProfilePage.css';

const CATEGORY_LABELS = {
  notes: 'Notes', gaming: 'Gaming', clothing: 'Clothing',
  electronics: 'Electronics', books: 'Books',
  sports: 'Sports', food: 'Food', other: 'Other',
};

const GENDER_LABELS = {
  male: 'Male', female: 'Female', other: 'Other',
  prefer_not_to_say: 'Prefer not to say',
};

export default function ProfilePage() {
  const { id }       = useParams();
  const { user: me } = useAuth();
  const navigate     = useNavigate();

  const [profile,  setProfile]  = useState(null);
  const [listings, setListings] = useState([]);
  const [loading,  setLoading]  = useState(true);

  const isOwnProfile = !id || id === me?._id;
  const targetId     = id || me?._id;

  useEffect(() => {
    if (!targetId) return;
    setLoading(true);
    api.get('/users/' + targetId)
      .then(({ data }) => {
        setProfile(data.user);
        setListings(data.listings);
      })
      .catch(() => toast.error('Profile not found.'))
      .finally(() => setLoading(false));
  }, [targetId]);

  const handleChat = async () => {
    if (!me) { navigate('/login'); return; }
    try {
      const { data } = await api.post('/chat/conversation', { recipientId: profile._id });
      navigate('/app/chat/' + data.conversation._id);
    } catch (err) {
      toast.error('Could not start chat.');
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner spinner-lg" /></div>;
  if (!profile) return (
    <div className="empty-state">
      <div className="empty-state-icon">😕</div>
      <h4>Profile not found</h4>
    </div>
  );

  return (
    <div className="profile-page fade-in">

      {/* Profile card */}
      <div className="profile-card card">
        <div className="profile-card-body">

          <div className="profile-top">
            {/* Avatar */}
            <div className="profile-avatar-wrap">
              {profile.avatar
                ? <img src={profile.avatar} alt={profile.name} className="avatar avatar-xl" />
                : <div className="avatar-placeholder avatar-xl profile-avatar-fallback">
                    {profile.name?.[0]}
                  </div>
              }
            </div>

            {/* Info */}
            <div className="profile-info">
              <div className="profile-name-row">
                <div>
                  <h2 className="profile-name">{profile.name}</h2>
                  {/* Username — prominent */}
                  <div className="profile-username">
                    <AtSign size={14} />
                    {profile.username || 'no username set'}
                  </div>
                </div>

                <div className="profile-actions-row">
                  {isOwnProfile ? (
                    <Link to="/app/profile/edit" className="btn btn-secondary btn-sm">
                      <Pencil size={13} /> Edit Profile
                    </Link>
                  ) : me && (
                    <button className="btn btn-primary btn-sm" onClick={handleChat}>
                      <MessageCircle size={13} /> Message
                    </button>
                  )}
                </div>
              </div>

              {/* Meta details */}
              <div className="profile-meta">
                <span className="profile-meta-item">
                  <Hash size={13} />
                  {profile.rollNumber || 'Roll number not set'}
                </span>
                <span className="profile-meta-item">
                  <MapPin size={13} />
                  {profile.location || 'Location not set'}
                </span>
                {profile.gender && profile.gender !== 'prefer_not_to_say' && (
                  <span className="profile-meta-item">
                    <Users size={13} />
                    {GENDER_LABELS[profile.gender]}
                  </span>
                )}
                <span className="profile-meta-item university">
                  {profile.university}
                </span>
              </div>
            </div>
          </div>

          {/* Stats row */}
          <div className="profile-stats">
            <div className="profile-stat">
              <span className="profile-stat-val" style={{ color: 'var(--accent)' }}>
                {profile.activeListings ?? 0}
              </span>
              <span className="profile-stat-lbl">Active Listings</span>
            </div>
            <div className="profile-stat">
              <span className="profile-stat-val" style={{ color: 'var(--success)' }}>
                {profile.sellCount ?? 0}
              </span>
              <span className="profile-stat-lbl">Total Sells</span>
            </div>
            <div className="profile-stat">
              <span className="profile-stat-val" style={{ color: 'var(--info)' }}>
                {new Date(profile.createdAt).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
              </span>
              <span className="profile-stat-lbl">Member Since</span>
            </div>
          </div>

        </div>
      </div>

      {/* Listings */}
      <div className="profile-listings-section">
        <h3>
          {isOwnProfile
            ? 'My Active Listings'
            : profile.name.split(' ')[0] + "'s Listings"
          }
          {listings.length > 0 && (
            <span className="badge badge-neutral" style={{ fontSize: '0.75rem', marginLeft: 8 }}>
              {listings.length}
            </span>
          )}
        </h3>

        {listings.length === 0 ? (
          <div className="empty-state card" style={{ padding: 'var(--space-10)' }}>
            <div className="empty-state-icon">📦</div>
            <p className="text-muted" style={{ fontSize: '0.875rem' }}>
              {isOwnProfile ? 'You have no active listings.' : 'No active listings.'}
            </p>
            {isOwnProfile && (
              <Link to="/app/sell" className="btn btn-primary" style={{ marginTop: 8 }}>
                List an Item
              </Link>
            )}
          </div>
        ) : (
          <div className="profile-listings-grid">
            {listings.map(product => (
              <Link key={product._id} to={'/app/product/' + product._id}
                className="profile-listing-card card card-hover">
                <div className="profile-listing-img">
                  {product.images?.[0]
                    ? <img src={product.images[0]} alt={product.title} loading="lazy" />
                    : <div className="profile-listing-no-img"><Package size={20} /></div>
                  }
                </div>
                <div className="profile-listing-info">
                  <span className={'badge badge-' + product.category}>
                    {CATEGORY_LABELS[product.category]}
                  </span>
                  <p className="profile-listing-title">{product.title}</p>
                  <p className="profile-listing-price">₹{product.price.toLocaleString()}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}